// src/utils/choixProblemeNcs.ts — SCE Issues
// « Déclarer une NC-S » depuis le bouton + (06/10) : une NC-S est TOUJOURS
// rattachée à un problème (photos, contexte, suivi, badge, N° retrouvé —
// décision de Guillaume). On choisit donc d'abord le problème : un existant,
// ou « Nouveau problème ». Cette règle dit lesquels proposer, dans quel ordre.
//
// Pur : aucun React, aucun réseau.

import { cleRapprochement, estActif, type Issue } from '@/types/issue.types'

/** Nombre de problèmes proposés. */
export const PROPOSES_MAX = 30

function ms(iso: string | undefined): number {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? 0 : t
}

/**
 * Les problèmes auxquels rattacher une NC-S.
 * - Jamais les annulés (écartés : rien à réclamer).
 * - Sans recherche : les actifs, du plus récent au plus ancien — une NC-S
 *   suit de près la découverte du défaut.
 * - Avec recherche (2 caractères et plus : N°, titre, N° de série, OF, PN),
 *   les clos aussi, actifs d'abord : une NC-S peut venir après coup.
 */
export function problemesPourNcs(issues: readonly Issue[], recherche: string): Issue[] {
  const cle = cleRapprochement(recherche)
  const candidats = issues.filter((i) => i.statut !== 'Annulé')
  const retenus =
    cle.length >= 2
      ? candidats.filter((i) => [i.id, i.titre, i.numeroSerie, i.of, i.pn].some((v) => cleRapprochement(v).includes(cle)))
      : candidats.filter((i) => estActif(i))
  return [...retenus]
    .sort(
      (a, b) =>
        Number(cleRapprochement(b.id) === cle) - Number(cleRapprochement(a.id) === cle) ||
        Number(estActif(b)) - Number(estActif(a)) ||
        ms(b.dateDeclaration) - ms(a.dateDeclaration),
    )
    .slice(0, PROPOSES_MAX)
}

// ─── Présentation en sections (06/10) ───

export interface SectionNcs {
  titre: string
  issues: Issue[]
}

/** Mes déclarations proposées en tête, au plus. */
const MES_MAX = 5

/**
 * Les problèmes proposés, en sections : sans recherche, « Mes déclarations »
 * (celui qui déclare la NC-S a souvent déclaré le problème) puis « En
 * cours » ; avec recherche, « Résultats ». Les sections vides disparaissent.
 * @param estAMoi le problème a-t-il été déclaré par la personne connectée ?
 */
export function sectionsPourNcs(issues: readonly Issue[], recherche: string, estAMoi: (i: Issue) => boolean): SectionNcs[] {
  const proposes = problemesPourNcs(issues, recherche)
  if (cleRapprochement(recherche).length >= 2) return proposes.length > 0 ? [{ titre: 'Résultats', issues: proposes }] : []
  const mes = proposes.filter(estAMoi).slice(0, MES_MAX)
  const autres = proposes.filter((i) => !mes.includes(i))
  return [
    { titre: 'Mes déclarations', issues: mes },
    { titre: mes.length > 0 ? 'Les autres en cours' : 'En cours', issues: autres },
  ].filter((s) => s.issues.length > 0)
}

/** « aujourd'hui », « hier », « il y a 3 j », « il y a 2 sem. », « il y a 4 mois ». */
export function ageCourt(iso: string, maintenant: number): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const jours = Math.floor((maintenant - t) / 86_400_000)
  if (jours <= 0) return "aujourd'hui"
  if (jours === 1) return 'hier'
  if (jours < 14) return `il y a ${jours} j`
  if (jours < 60) return `il y a ${Math.floor(jours / 7)} sem.`
  return `il y a ${Math.floor(jours / 30)} mois`
}
